import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { AlertCircle, CheckCircle, Lock } from "lucide-react";
import { motion } from 'framer-motion';
import { useTheme } from "../context/ThemeContext";
import { usePolling } from "../context/PollingContext";
import { useRegistrationRules } from "../contexts/RegistrationRulesContext";
import { CRITERIA_API, fetchWithAuth } from "../utils/api";
import RecordingIndicator from "../components/RecordingIndicator";
import UnifiedStationHeader from "../components/UnifiedStationHeader";
import { ScoringRulesBanner } from "../components/ScoringRulesInfo";

// New modular components
import StageBackground from "../components/ui/StageBackground";
import DanceParticles from "../components/ui/DanceParticles";
import SimpleScoringLayout from "../components/judging/SimpleScoringLayout";
import UnsavedChangesModal from "../components/UnsavedChangesModal";
import SimpleScoringLayoutSkeleton from "../components/judging/SimpleScoringLayoutSkeleton";

// Custom hooks
import { useJudgeTimer } from "../hooks/useJudgeTimer";
import { useJudgeScoring } from "../hooks/useJudgeScoring";
import { useJudgeProfiles } from "../hooks/useJudgeProfiles";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";

// Types
import {
  JudgeProfile,
  RoutineData,
  ScoreData as JudgeScoreData,
  ScoringRules,
  StyleScoreSheet,
  StyleCategory,
  StyleFeedback,
  ToastMessage,
  LayoutConfig,
  ScoringMode,
  JudgeStationPageProps
} from "../types/judge";
import { ScoreData } from "../context/PollingContext";

// Add debug logging utility
const DEBUG = false;
const debug = (message: string, data: any = null) => {
  if (!DEBUG) return;
  const timestamp = new Date().toISOString();
  if (data) {
    console.log(`[DEBUG ${timestamp}] ${message}`, data);
  } else {
    console.log(`[DEBUG ${timestamp}] ${message}`);
  }
};

// Helper function to parse time string (e.g., "3:30" to seconds)
const parseTimeToSeconds = (timeString: string | undefined): number => {
  if (!timeString) return 0;
  
  debug("Parsing time string:", timeString);
  
  const parts = timeString.toString().split(':');
  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    const totalSeconds = (minutes * 60) + seconds;
    debug(`Converted ${timeString} to ${totalSeconds} seconds`);
    return totalSeconds;
  }
  return 0;
};

// Helper function to format seconds to MM:SS
const formatSecondsToTime = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Helper to get song length from routine object
const getSongLength = (routine: RoutineData | null): string | undefined => {
  if (!routine) return undefined;
  
  // Try different possible field names
  return routine["Song Length"] || 
         routine.songLength || 
         routine["song length"] || 
         routine["SONG LENGTH"];
};

// Helper to get routine name from routine object
const getRoutineName = (routine: RoutineData | null): string => {
  if (!routine) return "Unknown";
  
  return routine["Routine Name"] || 
         routine.routineName || 
         routine.name || 
         "Unknown";
};

// Helper to get performer name from routine object (always returns empty for judge stations)
const getPerformerName = (routine: RoutineData | null): string => {
  // For judge stations, never show performer names to keep display clean and simple
  return '';
};

// Helper to get studio name from routine object
const getStudioName = (routine: RoutineData | null): string => {
  if (!routine) return "";
  
  // First check if there's already a studioName field
  if (routine.studioName) return routine.studioName;
  
  // Try to get from HitchKick data structure
  try {
    return routine.parentRoutine?.submissionRoutines?.[0]?.registrations?.studios?.businessName || '';
  } catch (error) {
    return '';
  }
};

// Helper to get studio code from routine object
const getStudioCode = (routine: RoutineData | null): string => {
  if (!routine) return "";
  
  // First check if there's already a studioCode field
  if (routine.studioCode) return routine.studioCode;
  
  // Try to get from HitchKick data structure
  try {
    const actualStudioCode = routine.parentRoutine?.submissionRoutines?.[0]?.studioCode;
    if (actualStudioCode) {
      return actualStudioCode;
    }
    // If no actual studio code, return empty string
    return '';
  } catch (error) {
    return '';
  }
};

// Helper to get routine style from routine object
const getRoutineStyle = (routine: RoutineData | null): string | null => {
  if (!routine) return null;
  
  // Debug: Log all routine fields to understand the data structure
  debug('Full routine object:', routine);
  
  // Try different possible field names for style
  const style = routine.division ||        // This seems to be where the dance style is stored
                routine.style || 
                routine.category || 
                routine.styleCategory || 
                routine.Category ||
                routine.Type ||
                routine.type ||
                routine['Dance Style'] ||
                routine['Style'] ||
                routine['STYLE'] ||
                routine['Category'];
  
  debug('Routine style extracted:', { 
    routine: getRoutineName(routine), 
    style,
    checkedFields: {
      division: routine.division,           // Added this field
      style: routine.style,
      category: routine.category,
      styleCategory: routine.styleCategory,
      Category: routine.Category,
      Type: routine.Type,
      type: routine.type,
      'Dance Style': routine['Dance Style'],
      'Style': routine['Style'],
      'STYLE': routine['STYLE']
    }
  });
  return style || null;
};

// Helper to generate a unique key for each routine's feedback
const getRoutineFeedbackKey = (routine: RoutineData | null): string => {
  if (!routine) return 'unknown';
  
  // Create a unique key based on routine name and index/id
  const routineName = getRoutineName(routine);
  const routineId = routine.id || routine.ID || routine['Routine ID'] || '';
  const style = getRoutineStyle(routine);
  
  // Combine routine identifier with style for uniqueness
  return `${routineName}-${routineId}-${style || 'nostyle'}`.replace(/\s+/g, '_').toLowerCase();
};

interface RouteParams extends Record<string, string | undefined> {
  competitionCode?: string;
  judgeId?: string;
  position?: string;
}

interface LocationState {
  judgeProfile?: JudgeProfile;
}

const JudgeStationPage: React.FC<JudgeStationPageProps> = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { competitionCode: urlCompetitionCode, judgeId, position } = useParams<RouteParams>();
  const location = useLocation() as { state?: LocationState };
  
  // Registration rules context for style score sheets
  const { state: registrationRulesState } = useRegistrationRules();
  const { styleScoreSheets, styleCategories, isLoading: registrationRulesLoading } = registrationRulesState;
  
  // State management
  const [judgeProfile, setJudgeProfile] = useState<JudgeProfile | null>(null);
  
  // Judge profiles hook for fallback loading
  const { judges: allJudgeProfiles, loading: judgeProfilesLoading, refreshJudges } = useJudgeProfiles();
  
  // Set page title with judge name
  useEffect(() => {
    const judgeName = judgeProfile?.name || 'Judge';
    document.title = `Dance Digital | ${judgeName}`;
    return () => {
      document.title = 'Dance Digital'; // Reset on unmount
    };
  }, [judgeProfile?.name]);

  // Debug judge profile changes
  useEffect(() => {
    console.log("JudgeStationPage - judgeProfile state changed:", judgeProfile);
  }, [judgeProfile]);
  const [judgeLoading, setJudgeLoading] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [isBlurred, setIsBlurred] = useState<boolean>(false);
  const [scoringRules, setScoringRules] = useState<ScoringRules | null>(null);
  // Store style feedback per routine - key is routine identifier, value is feedback object
  const [styleFeedbackByRoutine, setStyleFeedbackByRoutine] = useState<Record<string, StyleFeedback>>({});
  // Force simple scoring mode for competition - no dynamic loading
  const scoringMode: ScoringMode = 'simple';
  
  // Track locked routines (routines that have been scored and submitted by this judge)
  const [lockedRoutines, setLockedRoutines] = useState<Set<string>>(new Set());
  
  // Polling context
  const { 
    connected, 
    loading,
    competitionCode,
    currentRoutine,
    currentIndex,
    scheduleData: routines,
    connectedJudges,
    judgeProfiles,
    judgePositions,
    recordingStatus,
    scoreUpdates,
    startSession,
    setJudgeProfile: updateJudgeProfile,
    changeCurrentRoutine,
    updateScore,
    submitScore,
    uploadSchedule,
    // Auto-reconnection status
    isReconnecting,
    reconnectAttempts
  } = usePolling();
  
  
  // Get contextJudgeId from polling context if available
  const contextJudgeId = null; // Not used in polling context
  
  const [socketStatus, setSocketStatus] = useState<string>("Disconnected");
  
  // Use judge ID from URL or context if available
  const effectiveJudgeId = judgeId || contextJudgeId || "judge1";
  // Use competition code from URL or context
  const effectiveCompetitionCode = urlCompetitionCode || competitionCode;
  // Use position from polling context (judgePositions or judgeProfiles) or URL as fallback
  const effectivePosition = useMemo(() => {
    // First try to get position from polling context judgePositions
    if (effectiveJudgeId && judgePositions && judgePositions[effectiveJudgeId]) {
      return String(judgePositions[effectiveJudgeId]);
    }
    
    // Second, try to get position from judgeProfiles.assignedPosition
    if (effectiveJudgeId && judgeProfiles && judgeProfiles[effectiveJudgeId]?.assignedPosition) {
      return String(judgeProfiles[effectiveJudgeId].assignedPosition);
    }
    
    // Fallback to URL position parameter
    return position;
  }, [effectiveJudgeId, judgePositions, judgeProfiles, position]);

  // Convert Routine to RoutineData type
  const convertRoutineToRoutineData = (routine: any): RoutineData | null => {
    if (!routine) return null;
    return {
      ...routine,
      routineNumber: routine.number || routine.routineNumber || routine.id || "unknown"
    } as RoutineData;
  };

  // Convert current routine for compatibility
  const currentRoutineData = convertRoutineToRoutineData(currentRoutine);

  // No need to update local state - using polling data directly
  
  // Custom hooks
  const { timer, startTimer, stopTimer, resetTimer } = useJudgeTimer();
  const { 
    scores, 
    comments, 
    isSubmitted, 
    lastUpdateTime, 
    updateScore: updateScoreState, 
    updateComments, 
    submitScores, 
    resetScoring,
    isDirty,
    loadExistingScores
  } = useJudgeScoring();
  
  // Helper function to check if a routine is locked (already scored by this judge)
  const isRoutineLocked = useCallback((routineId: string): boolean => {
    if (!effectiveJudgeId || !scoreUpdates) return false;
    
    const scoreKey = `${routineId}_${effectiveJudgeId}`;
    const scoreData = scoreUpdates[scoreKey];
    
    // A routine is locked if this judge has submitted scores for it
    const isLocked = scoreData?.isSubmitted === true;
    
    
    return isLocked;
  }, [effectiveJudgeId, scoreUpdates]);
  
  // Helper function to get the next available (unlocked) routine
  const getNextAvailableRoutine = useCallback((): number | null => {
    if (!routines || routines.length === 0) return null;
    
    // Start from the routine after current
    for (let i = currentIndex + 1; i < routines.length; i++) {
      const routine = routines[i];
      const routineId = routine.number || routine.id || routine.routineNumber;
      if (routineId && !isRoutineLocked(String(routineId))) {
        return i;
      }
    }
    
    // If no unlocked routines found after current, check from beginning
    for (let i = 0; i < currentIndex; i++) {
      const routine = routines[i];
      const routineId = routine.number || routine.id || routine.routineNumber;
      if (routineId && !isRoutineLocked(String(routineId))) {
        return i;
      }
    }
    
    return null; // All routines are locked
  }, [routines, currentIndex, isRoutineLocked]);
  
  // Custom unsaved changes modal
  const {
    showModal: showUnsavedModal,
    checkUnsavedChanges,
    confirmNavigation,
    cancelNavigation,
    saveAndContinue
  } = useUnsavedChanges({
    isDirty,
    onSave: async () => {
      // If there are scores to save, submit them
      if (isDirty && (Object.values(scores).some(score => score > 0) || comments.trim().length > 0)) {
        handleScoreSubmit(scores, comments);
      }
    },
    message: "You have unsaved scores that will be lost if you continue. Would you like to save them first?",
    showSaveOption: true
  });
  
  // Remove browser beforeunload - we'll use our custom modal instead
  useEffect(() => {
    // Disable browser's default beforeunload behavior
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Always prevent the browser's default dialog
      // Our custom modal will handle this instead
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    // Only add listener to prevent browser dialog, but don't show any message
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  
  // Desktop-only layout configuration
  const layout: LayoutConfig = {
    containerPadding: 'px-6 py-4',
    headerPadding: 'px-6 py-4',
    cardPadding: 'p-6',
    gap: 'gap-6',
    gridCols: 'grid-cols-10',
    scoringSpan: 'col-span-4',
    totalSpan: 'col-span-3',
    scheduleSpan: 'col-span-3',
    fontSize: 'text-base',
    headerSize: 'text-2xl',
    buttonSize: 'p-3',
    iconSize: 20,
    judgeAvatarSize: 'w-12 h-12',
    judgeAvatarGap: 'gap-3',
    judgeAvatarMargin: 'ml-4',
    judgeNameSize: 'text-lg',
    judgeTitleSize: 'text-sm',
    judgeNameVisibility: 'block',
    onlineIndicatorSize: 'w-4 h-4',
    scale: 1
  };

  // Save theme preference
  useEffect(() => {
    localStorage.setItem("judge_dark_mode", isDarkMode.toString());
  }, [isDarkMode]);

  // Polling connection status
  useEffect(() => {
    if (connected) {
      setSocketStatus("Connected");
      debug("Polling connected");
      
      // In the simplified approach, session is already started from CompetitionCreation
      // No need to join again - just log the connection
      if (effectiveCompetitionCode && effectiveJudgeId) {
        debug(`Connected to session ${effectiveCompetitionCode} as Judge ${effectiveJudgeId}`);
      }
    } else {
      setSocketStatus("Disconnected");
      debug("Polling disconnected");
    }
  }, [connected, effectiveJudgeId, effectiveCompetitionCode]);

  // Extract judge profile from location state or sessionStorage (for new tab access)
  useEffect(() => {
    if (location.state?.judgeProfile) {
      console.log("JudgeStationPage - Found judge profile in location state:", location.state.judgeProfile);
      console.log("JudgeStationPage - Judge profile defaultNote:", location.state.judgeProfile?.defaultNote);
      setJudgeProfile(location.state.judgeProfile);
      
      // Note: No need to broadcast profile when accessing via eye icon
      // The profile already exists in DynamoDB from original assignment
      // and other clients can see it via polling
    } else if (effectiveJudgeId) {
      // Check sessionStorage for judge profile data (used when opened in new tab)
      const sessionKey = `judgeStation_${effectiveJudgeId}`;
      const storedData = sessionStorage.getItem(sessionKey);
      
      if (storedData) {
        try {
          const judgeStationData = JSON.parse(storedData);
          console.log("JudgeStationPage - Found judge profile in sessionStorage:", judgeStationData.judgeProfile);
          setJudgeProfile(judgeStationData.judgeProfile);
          
          // Clean up sessionStorage after use
          sessionStorage.removeItem(sessionKey);
        } catch (error) {
          console.error("JudgeStationPage - Error parsing sessionStorage data:", error);
        }
      } else {
        // Fallback: Check if the judge profile is available in the polling context
        const pollingJudgeProfile = judgeProfiles[effectiveJudgeId];
        if (pollingJudgeProfile) {
          console.log("JudgeStationPage - Found judge profile in polling context:", pollingJudgeProfile);
          setJudgeProfile(pollingJudgeProfile);
        } else if (allJudgeProfiles.length > 0) {
          // Fallback: Look for judge profile in the fetched judge profiles
          const apiJudgeProfile = (allJudgeProfiles as JudgeProfile[]).find(judge => judge.id === effectiveJudgeId);
          if (apiJudgeProfile) {
            console.log("JudgeStationPage - Found judge profile in API data:", apiJudgeProfile);
            setJudgeProfile(apiJudgeProfile);
          } else {
            console.log("JudgeStationPage - No judge profile found, will use effectiveJudgeId as fallback:", effectiveJudgeId);
            // Set a minimal profile with just the ID so the avatar can at least show initials
            setJudgeProfile({
              id: effectiveJudgeId,
              name: effectiveJudgeId,
              title: '',
              defaultNote: ''
            });
          }
        } else if (!judgeProfilesLoading) {
          // If no profiles loaded yet and not loading, trigger a fetch
          console.log("JudgeStationPage - No judge profiles available, fetching from API...");
          refreshJudges();
        }
      }
    }
  }, [location.state, effectiveJudgeId, connected, updateJudgeProfile, judgeProfiles, allJudgeProfiles, judgeProfilesLoading, refreshJudges]);

  // Schedule updates are handled automatically by polling
  // No need for WebSocket listeners

  // Current routine changes and judges updates are handled automatically by polling
  // No need for WebSocket listeners

  // Score submissions and scoring rules updates are handled automatically by polling
  // No need for WebSocket listeners

  // Timer events are handled automatically by polling
  // No need for WebSocket listeners

  // Track which routine we've loaded scores for to prevent infinite loops
  const [loadedRoutineId, setLoadedRoutineId] = useState<string | null>(null);

  // When routine changes, load existing scores or reset scoring and timer
  useEffect(() => {
    if (currentRoutineData) {
      // Check for existing submitted scores for this routine and judge
      const routineId = currentRoutineData.number || currentRoutineData.id || currentRoutineData.name;
      
      // Prevent infinite loop by checking if we've already loaded scores for this routine
      if (routineId && routineId !== loadedRoutineId) {
        setLoadedRoutineId(routineId);
        
        if (effectiveJudgeId && scoreUpdates) {
          const scoreKey = `${routineId}_${effectiveJudgeId}`;
          const existingScore = scoreUpdates[scoreKey];
          
          if (existingScore && existingScore.isSubmitted) {
            console.log('JudgeStationPage: Loading existing submitted scores for routine:', routineId);
            // Load existing scores using the hook's loadExistingScores function
            loadExistingScores(
              existingScore.scores || {},
              existingScore.comments || '',
              true, // isSubmitted
              existingScore.submittedTime
            );
          } else {
            console.log('JudgeStationPage: No existing submitted scores found, resetting scoring state');
            // Reset scoring state if no existing scores
            resetScoring();
          }
        } else {
          // Reset scoring state if we don't have the necessary data
          resetScoring();
        }
        
        // Reset timer state
        stopTimer();
        const songLength = getSongLength(currentRoutineData);
        
        if (songLength) {
          debug("Found song length for routine:", songLength);
          const seconds = parseTimeToSeconds(songLength);
          resetTimer(seconds);
        } else {
          debug("No song length found for routine:", currentRoutineData);
          resetTimer(0);
        }
      }
    }
  }, [currentRoutineData, resetScoring, stopTimer, resetTimer, effectiveJudgeId, scoreUpdates, loadExistingScores, loadedRoutineId]);

  // Handle routine navigation
  const handlePreviousRoutine = useCallback(() => {
    if (currentIndex > 0) {
      const navigateToRoutine = () => {
        // Find the previous unlocked routine
        let newIndex = currentIndex - 1;
        let foundUnlocked = false;
        
        // Search backwards for an unlocked routine
        while (newIndex >= 0) {
          const routine = routines[newIndex];
          const routineId = routine?.number || routine?.id || routine?.routineNumber;
          
          if (routineId && !isRoutineLocked(String(routineId))) {
            foundUnlocked = true;
            break;
          }
          newIndex--;
        }
        
        if (foundUnlocked && newIndex >= 0) {
          const newRoutine = routines[newIndex];
          if (newRoutine) {
            // Use the PollingContext to change routine (this updates local state only for judges)
            changeCurrentRoutine({
              index: newIndex,
              routine: newRoutine
            });
            
            console.log('Judge navigated to previous routine:', newIndex);
          }
        } else {
          setToast({
            type: 'warning',
            message: 'No previous unlocked routines available'
          });
        }
      };

      // Use custom unsaved changes check
      checkUnsavedChanges(navigateToRoutine);
    }
  }, [currentIndex, routines, checkUnsavedChanges, changeCurrentRoutine, isRoutineLocked]);

  const handleNextRoutine = useCallback(() => {
    if (currentIndex < routines.length - 1) {
      const navigateToRoutine = () => {
        // Find the next unlocked routine
        let newIndex = currentIndex + 1;
        let foundUnlocked = false;
        
        // Search forwards for an unlocked routine
        while (newIndex < routines.length) {
          const routine = routines[newIndex];
          const routineId = routine?.number || routine?.id || routine?.routineNumber;
          
          if (routineId && !isRoutineLocked(String(routineId))) {
            foundUnlocked = true;
            break;
          }
          newIndex++;
        }
        
        if (foundUnlocked && newIndex < routines.length) {
          const newRoutine = routines[newIndex];
          if (newRoutine) {
            // Use the PollingContext to change routine (this updates local state only for judges)
            changeCurrentRoutine({
              index: newIndex,
              routine: newRoutine
            });
            
            console.log('Judge navigated to next routine:', newIndex);
          }
        } else {
          setToast({
            type: 'warning',
            message: 'No next unlocked routines available'
          });
        }
      };

      // Use custom unsaved changes check
      checkUnsavedChanges(navigateToRoutine);
    }
  }, [currentIndex, routines, checkUnsavedChanges, changeCurrentRoutine, isRoutineLocked]);

  // Handle routine selection from PerformanceSchedule
  const handleRoutineSelect = useCallback((index: number) => {
    if (Array.isArray(routines) && index >= 0 && index < routines.length) {
      const selectedRoutine = routines[index];
      const routineId = selectedRoutine?.number || selectedRoutine?.id || selectedRoutine?.routineNumber;
      
      // Check if the routine is locked
      if (routineId && isRoutineLocked(String(routineId))) {
        setToast({
          type: 'warning',
          message: 'This routine has already been scored and is locked'
        });
        return;
      }
      
      const navigateToRoutine = () => {
        // Judges can select different routines locally
        // This will trigger sync notification if they're different from tabulator
        if (selectedRoutine) {
          // Use the PollingContext to change routine (this updates local state only for judges)
          changeCurrentRoutine({
            index: index,
            routine: selectedRoutine
          });
          
          console.log('Judge selected routine:', index);
        }
      };

      // Use custom unsaved changes check
      checkUnsavedChanges(navigateToRoutine);
    }
  }, [routines, checkUnsavedChanges, changeCurrentRoutine, isRoutineLocked]);

  // Track processed feedback to prevent infinite loops - declare refs first
  const processedFeedbackRef = useRef<Set<string>>(new Set());
  const lastProcessedHashRef = useRef<string>('');
  const lastRoutineIdRef = useRef<string>('');
  const stableFeedbackRef = useRef<StyleFeedback>({});

  // Helper function to convert grouped styleFeedback to individual tag format
  const convertGroupedFeedbackToIndividual = useCallback((groupedFeedback: any): StyleFeedback => {
    if (!groupedFeedback || typeof groupedFeedback !== 'object') {
      return {};
    }

    const individualFeedback: StyleFeedback = {};

    // Check if this is already in individual format (key-value pairs where values are 'good' or 'needs-work')
    const isIndividualFormat = Object.values(groupedFeedback).some(value => 
      value === 'good' || value === 'needs-work'
    );

    if (isIndividualFormat) {
      // Already in the correct format, just return it
      return groupedFeedback as StyleFeedback;
    }

    // Handle the DynamoDB format
    if (groupedFeedback.good && groupedFeedback.good.L && Array.isArray(groupedFeedback.good.L)) {
      groupedFeedback.good.L.forEach((item: any) => {
        if (item && item.M && item.M.category && item.M.category.S && item.M.criteria && item.M.criteria.S) {
          const key = `${item.M.category.S}:${item.M.criteria.S}`;
          individualFeedback[key] = 'good';
        }
      });
    }

    if (groupedFeedback['needs-work'] && groupedFeedback['needs-work'].L && Array.isArray(groupedFeedback['needs-work'].L)) {
      groupedFeedback['needs-work'].L.forEach((item: any) => {
        if (item && item.M && item.M.category && item.M.category.S && item.M.criteria && item.M.criteria.S) {
          const key = `${item.M.category.S}:${item.M.criteria.S}`;
          individualFeedback[key] = 'needs-work';
        }
      });
    }

    // Handle the simplified grouped format (for backward compatibility)
    if (groupedFeedback?.good && Array.isArray(groupedFeedback.good)) {
      groupedFeedback.good.forEach((item: any) => {
        if (typeof item === 'string') {
          // Legacy format - just the criteria text
          individualFeedback[item] = 'good';
        } else if (item && typeof item === 'object' && item.category && item.criteria) {
          // Simplified format - object with category and criteria
          const key = `${item.category}:${item.criteria}`;
          individualFeedback[key] = 'good';
        }
      });
    }
    
    if (groupedFeedback?.['needs-work'] && Array.isArray(groupedFeedback['needs-work'])) {
      groupedFeedback['needs-work'].forEach((item: any) => {
        if (typeof item === 'string') {
          // Legacy format - just the criteria text
          individualFeedback[item] = 'needs-work';
        } else if (item && typeof item === 'object' && item.category && item.criteria) {
          // Simplified format - object with category and criteria
          const key = `${item.category}:${item.criteria}`;
          individualFeedback[key] = 'needs-work';
        }
      });
    }
    
    console.log('🔄 Style feedback converted from grouped to individual:', {
      input: groupedFeedback,
      output: individualFeedback
    });
    
    return individualFeedback;
  }, []);

  // Get current routine's feedback - memoized to prevent infinite re-renders
  const currentRoutineFeedback = useMemo((): StyleFeedback => {
    if (!currentRoutineData) {
      stableFeedbackRef.current = {};
      return stableFeedbackRef.current;
    }
    
    const routineId = currentRoutineData.number || currentRoutineData.id;
    const routineKey = `routine-${routineId}`;
    const feedback = styleFeedbackByRoutine[routineKey] || {};
    
    // Only update the stable ref if the content has actually changed
    const feedbackHash = JSON.stringify(feedback);
    const currentHash = JSON.stringify(stableFeedbackRef.current);
    
    if (feedbackHash !== currentHash) {
      stableFeedbackRef.current = feedback;
    }
    
    return stableFeedbackRef.current;
  }, [currentRoutineData?.number, currentRoutineData?.id, styleFeedbackByRoutine]);

  // Keep the function for backward compatibility but make it stable
  const getCurrentRoutineFeedback = useCallback((): StyleFeedback => {
    return currentRoutineFeedback;
  }, [currentRoutineFeedback]);

  // Clear processed feedback when routine actually changes (not just re-renders)
  useEffect(() => {
    const currentRoutineId = currentRoutineData?.number || currentRoutineData?.id || '';
    
    // Only clear if the routine ID actually changed
    if (currentRoutineId && currentRoutineId !== lastRoutineIdRef.current) {
      console.log('🔄 Routine changed, clearing processed feedback:', {
        from: lastRoutineIdRef.current,
        to: currentRoutineId
      });
      processedFeedbackRef.current.clear();
      lastProcessedHashRef.current = '';
      lastRoutineIdRef.current = currentRoutineId;
      stableFeedbackRef.current = {}; // Clear stable feedback ref too
    }
  }, [currentRoutineData?.number, currentRoutineData?.id]);

  // Create a stable reference to the specific score we care about
  const currentScoreKey = useMemo(() => {
    if (!currentRoutineData || !effectiveJudgeId) return null;
    const routineId = currentRoutineData.number || currentRoutineData.id;
    return routineId ? `${routineId}_${effectiveJudgeId}` : null;
  }, [currentRoutineData?.routineNumber, currentRoutineData?.id, effectiveJudgeId]);

  const currentScoreData = useMemo(() => {
    if (!currentScoreKey || !scoreUpdates) return null;
    return scoreUpdates[currentScoreKey] || null;
  }, [currentScoreKey, scoreUpdates]);

  // Memoize the relevant score data to prevent unnecessary re-processing
  const relevantScoreData = useMemo(() => {
    if (!currentScoreData || !currentScoreData.styleFeedback || !currentScoreKey) return null;
    
    // Extract routine ID from the scoreKey instead of depending on currentRoutineData
    const routineId = currentScoreKey.split('_')[0];
    if (!routineId) return null;
    
    // Create a stable hash to detect actual changes in styleFeedback content
    const feedbackHash = JSON.stringify(currentScoreData.styleFeedback);
    const timestamp = currentScoreData.timestamp || currentScoreData.lastUpdated || currentScoreData.submittedTime;
    
    return {
      scoreKey: currentScoreKey,
      routineId,
      styleFeedback: currentScoreData.styleFeedback,
      timestamp,
      feedbackHash
    };
  }, [currentScoreKey, currentScoreData]);

  // Load existing styleFeedback when relevant score data changes
  useEffect(() => {
    if (!relevantScoreData) return;
    
    const { scoreKey, routineId, styleFeedback, timestamp, feedbackHash } = relevantScoreData;
    
    // Create a stable process key using the pre-computed hash
    const processKey = `${scoreKey}_${feedbackHash}`;
    
    // Triple-check: prevent processing the same feedback hash multiple times
    if (lastProcessedHashRef.current === feedbackHash) {
      return; // Skip if we just processed this exact feedback
    }
    
    // Check if we've already processed this exact feedback
    const alreadyProcessed = processedFeedbackRef.current.has(processKey);
    
    // Only process if we haven't already processed this exact feedback
    if (!alreadyProcessed) {
      // Debounce the processing to prevent rapid-fire updates
      const timeoutId = setTimeout(() => {
        const convertedFeedback = convertGroupedFeedbackToIndividual(styleFeedback);
        
        // Generate routine key using the routine ID from relevantScoreData
        const routineKey = `routine-${routineId}`;
        
        setStyleFeedbackByRoutine(prev => {
          // Only update if the content has actually changed
          const currentFeedback = prev[routineKey] || {};
          const currentFeedbackHash = JSON.stringify(currentFeedback);
          const newFeedbackHash = JSON.stringify(convertedFeedback);
          
          if (currentFeedbackHash === newFeedbackHash) {
            return prev; // No change, return same object
          }
          
          return {
            ...prev,
            [routineKey]: convertedFeedback
          };
        });
        
        // Mark this feedback as processed
        processedFeedbackRef.current.add(processKey);
        lastProcessedHashRef.current = feedbackHash;
        
        // Limit the size of the processed set to prevent memory leaks
        if (processedFeedbackRef.current.size > 100) {
          const oldestKeys = Array.from(processedFeedbackRef.current).slice(0, 50);
          oldestKeys.forEach(key => processedFeedbackRef.current.delete(key));
        }
      }, 100); // 100ms debounce
      
      // Cleanup timeout on unmount or if effect runs again
      return () => clearTimeout(timeoutId);
    }
  }, [relevantScoreData, convertGroupedFeedbackToIndividual]);

  // Handle style-specific feedback updates - store per routine
  const handleStyleFeedbackUpdate = useCallback((newFeedback: StyleFeedback) => {
    if (!currentRoutineData) return;
    
    const routineId = currentRoutineData.number || currentRoutineData.id;
    const routineKey = `routine-${routineId}`;
    
    setStyleFeedbackByRoutine(prev => ({
      ...prev,
      [routineKey]: newFeedback
    }));
    
    // You might want to save this to the server or include it in the score submission
    // For now, we'll just store it locally
    if (process.env.NODE_ENV === 'development') {
      debug('Style feedback updated for routine:', {
        routine: getRoutineName(currentRoutineData),
        style: getRoutineStyle(currentRoutineData),
        key: routineKey,
        feedback: newFeedback
      });
    }
  }, [currentRoutineData?.number, currentRoutineData?.id]);

  // Debug logging for style score sheets and current routine
  useEffect(() => {
    debug('Style score sheets available:', styleScoreSheets);
    debug('Style categories available:', styleCategories);
  }, [styleScoreSheets, styleCategories]);

  useEffect(() => {
    if (currentRoutineData) {
      const routineStyle = getRoutineStyle(currentRoutineData);
      const routineKey = getRoutineFeedbackKey(currentRoutineData);
      const currentFeedback = styleFeedbackByRoutine[routineKey] || {};
      
      debug('Current routine changed:', {
        routine: getRoutineName(currentRoutineData),
        style: routineStyle,
        feedbackKey: routineKey,
        existingFeedback: Object.keys(currentFeedback).length > 0 ? currentFeedback : 'No existing feedback',
        availableStyleSheets: styleScoreSheets.map((sheet: any) => ({
          styleId: sheet.styleId,
          styleName: sheet.styleName || 'Unnamed'
        }))
      });
    }
  }, [currentRoutineData, styleScoreSheets, styleFeedbackByRoutine]);


  // Helper function to determine if style-specific scoring is available for a routine
  const hasStyleSpecificScoring = useCallback((routine: RoutineData | null, sheets: StyleScoreSheet[]): boolean => {
    if (!routine || !sheets || sheets.length === 0) {
      return false;
    }
    
    // Get routine style using the same logic as StyleSpecificScoringCard
    const routineStyle = routine.category ||           // Check category field first (contains "Jazz")
                        routine.style || 
                        routine.styleCategory || 
                        routine['Dance Style'] ||
                        routine['Style'] ||
                        routine.division ||            // Check division later (contains "Solo")
                        routine.Category ||
                        routine.Type ||
                        routine.type;
    
    if (!routineStyle) {
      return false;
    }
    
    // Find matching style score sheet using the same logic as StyleSpecificScoringCard
    const matchingSheet = sheets.find(sheet => {
      if (!sheet.styleId) return false;
      
      const styleKey = routineStyle.toLowerCase().trim();
      const sheetStyleKey = sheet.styleId.toLowerCase().trim();
      
      // Check for exact match or partial matches
      return sheetStyleKey === styleKey || 
             styleKey.includes(sheetStyleKey) || 
             sheetStyleKey.includes(styleKey);
    });
    
    return !!matchingSheet;
  }, []);

  // Scoring mode is now hardcoded to 'simple' - no API fetching needed for competition reliability

  // Handle CSV upload
  const handleRoutinesUpload = useCallback(async (newRoutines: RoutineData[]) => {
    if (Array.isArray(newRoutines) && newRoutines.length > 0) {
      // Log the first routine to check its structure
      debug("First routine from CSV:", newRoutines[0]);
      debug("Available fields:", Object.keys(newRoutines[0]));
      debug("Song Length field value:", getSongLength(newRoutines[0]));
      
      // Ensure routineNumber is present for compatibility
      const processedRoutines = newRoutines.map((routine: any, index: number) => ({
        ...routine,
        id: String(routine.id || `routine-${index}`),
        routineNumber: routine.number || routine.routineNumber || routine.id || routine['Routine #'] || `routine-${index}`
      }));
      
      // Upload schedule using polling context
      try {
        await uploadSchedule(processedRoutines);
        setToast({
          type: 'success',
          message: `Loaded ${newRoutines.length} routines successfully`
        });
      } catch (error) {
        console.error("Error uploading schedule:", error);
        setToast({
          type: 'error',
          message: 'Failed to upload schedule'
        });
      }
    }
  }, [uploadSchedule]);


  // Handle score updates
  const handleScoreUpdate = useCallback((newScores: JudgeScoreData, isSubmit: boolean = false, allCriteriaScored?: boolean) => {
    updateScoreState(newScores, isSubmit);
    
    if (isSubmit) {
      setToast({
        type: 'success',
        message: 'Scores submitted successfully'
      });
    }
    
    // Update scores in the WebSocket context
    if (connected && currentRoutine) {
      const routineId = currentRoutine.number || currentRoutine.id;
      if (routineId) {
        updateScore({
          judgeId: effectiveJudgeId,
          routineId: String(routineId),
          routineIndex: currentIndex,
          scores: newScores,
          comments: comments,
          isSubmitted: isSubmit
        });
      }
    }
  }, [connected, currentRoutine, currentIndex, comments, effectiveJudgeId, updateScore, updateScoreState]);

  // Handle score submission
  const handleScoreSubmit = useCallback((submittedScores: JudgeScoreData, submittedComments: string, booleanFlags?: { iChoreographedThis?: boolean; notFamilyFriendly?: boolean }) => {
    submitScores(submittedScores, submittedComments);
    
    // Submit final scores through WebSocket context
    if (connected && currentRoutine) {
      const routineId = currentRoutine.number || currentRoutine.id;
      
      if (routineId) {
        // Get current routine's style feedback
        const currentFeedback = currentRoutineFeedback;
        
        submitScore({
          judgeId: effectiveJudgeId,
          routineId: String(routineId),
          routineIndex: currentIndex,
          scores: submittedScores,
          comments: submittedComments,
          submittedTime: new Date().toISOString(),
          isSubmitted: true,
          // Include current routine data to avoid backend lookup
          routineData: currentRoutine ? {
            routineNumber: String(currentRoutine.number || currentRoutine.routineNumber || routineId),
            routineId: String(currentRoutine.id || routineId),
            name: currentRoutine.name || currentRoutine.title || `Routine ${routineId}`,
            performerName: currentRoutine.performerName || '',
            studioCode: currentRoutine.studioCode || '',
            division: currentRoutine.division || '',
            ageGroup: currentRoutine.ageGroup || '',
            category: currentRoutine.category || '',
            dancersCount: String(currentRoutine.dancersCount || '')
          } : undefined,
          // Include boolean flags if provided
          ...(booleanFlags?.iChoreographedThis !== undefined && { iChoreographedThis: booleanFlags.iChoreographedThis }),
          ...(booleanFlags?.notFamilyFriendly !== undefined && { notFamilyFriendly: booleanFlags.notFamilyFriendly }),
          ...(currentFeedback && Object.keys(currentFeedback).length > 0 && { 
            styleFeedback: (() => {
              const good: Array<{category: string, criteria: string}> = [];
              const needsWork: Array<{category: string, criteria: string}> = [];
              
              Object.entries(currentFeedback).forEach(([key, feedbackType]) => {
                const [category, criteria] = key.split(':', 2);
                if (!category || !criteria) return;
                
                const feedbackItem = { category, criteria };
                
                if (feedbackType === 'good') {
                  good.push(feedbackItem);
                } else if (feedbackType === 'needs-work') {
                  needsWork.push(feedbackItem);
                }
              });
              
              const result: {
                good?: Array<{category: string, criteria: string}>;
                'needs-work'?: Array<{category: string, criteria: string}>;
              } = {};
              if (good.length > 0) result.good = good;
              if (needsWork.length > 0) result['needs-work'] = needsWork;
              
              return result;
            })()
          })
        });
        
        // Score submission is handled by the polling context
        // No need for additional socket emit
      }
    }
    
    // Clear any browser form dirty state by resetting all form elements on the page
    setTimeout(() => {
      const forms = document.querySelectorAll('form');
      forms.forEach(form => {
        // Mark form as clean to prevent browser warnings
        const inputs = form.querySelectorAll('input, textarea, select');
        inputs.forEach(input => {
          if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
            input.defaultValue = input.value;
          } else if (input instanceof HTMLSelectElement) {
            // For select elements, mark the current option as selected by default
            const selectedOption = input.options[input.selectedIndex];
            if (selectedOption) {
              selectedOption.defaultSelected = true;
            }
          }
        });
      });
      
      // Also handle standalone inputs not in forms
      const inputs = document.querySelectorAll('input:not(form input), textarea:not(form textarea), select:not(form select)');
      inputs.forEach(input => {
        if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
          input.defaultValue = input.value;
        } else if (input instanceof HTMLSelectElement) {
          const selectedOption = input.options[input.selectedIndex];
          if (selectedOption) {
            selectedOption.defaultSelected = true;
          }
        }
      });
    }, 100);
    
    setToast({
      type: 'success',
      message: 'Scores submitted successfully'
    });
    
    // Auto-advance to next available routine after successful submission
    setTimeout(() => {
      const nextRoutineIndex = getNextAvailableRoutine();
      if (nextRoutineIndex !== null && nextRoutineIndex !== currentIndex) {
        const nextRoutine = routines[nextRoutineIndex];
        if (nextRoutine) {
          changeCurrentRoutine({
            index: nextRoutineIndex,
            routine: nextRoutine
          });
          
          setToast({
            type: 'info',
            message: `Advanced to routine ${nextRoutine.number || nextRoutine.id || nextRoutineIndex + 1}`
          });
          
          console.log('Auto-advanced to next routine:', nextRoutineIndex);
        }
      } else {
        // No more unlocked routines available
        setToast({
          type: 'info',
          message: 'All available routines have been scored'
        });
      }
    }, 1000); // Small delay to allow score submission to complete
    
  }, [connected, currentRoutine, currentIndex, effectiveJudgeId, submitScore, submitScores, currentRoutineFeedback, getNextAvailableRoutine, routines, changeCurrentRoutine]);

  // Calculate progress percentage for timer
  const timerProgress = timer.totalSeconds > 0 ? ((timer.totalSeconds - timer.seconds) / timer.totalSeconds) * 100 : 0;

  // Handle blur toggle for privacy lock screen
  const toggleBlur = (override?: boolean) => {
    const newBlurState = override !== undefined ? override : !isBlurred;
    setIsBlurred(newBlurState);
    setToast({
      type: 'info',
      message: `Screen ${newBlurState ? 'locked' : 'unlocked'}`
    });
  };

  // Update keyboard shortcut for privacy lock screen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'l') {
        event.preventDefault();
        toggleBlur();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isBlurred]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Initialize judge session with proper role
  useEffect(() => {
    const initializeJudgeSession = async () => {
      if (urlCompetitionCode && effectiveJudgeId && effectivePosition && !loading) {
        console.log('Initializing judge session:', {
          competitionCode: urlCompetitionCode,
          judgeId: effectiveJudgeId,
          position: effectivePosition
        });
        
        try {
          // Start session as judge with the competition code
          await startSession(
            urlCompetitionCode, 
            'judge', 
            routines, // Use current routines from polling context
            effectiveJudgeId,
            null, // judgeProfile - will be set separately if needed
            effectivePosition
          );
          console.log('Judge session initialized successfully');
        } catch (error) {
          console.error('Failed to initialize judge session:', error);
        }
      }
    };

    initializeJudgeSession();
  }, [urlCompetitionCode, effectiveJudgeId, effectivePosition, loading, startSession, routines]);

  // Show loading screen while competition data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Loading Competition</h2>
          <p className="text-white/60">
            {urlCompetitionCode ? `Loading ${urlCompetitionCode}...` : 'Fetching competition data...'}
          </p>
          <p className="text-white/40 text-sm mt-2">
            Judge: {effectiveJudgeId} | Position: {effectivePosition}
          </p>
        </div>
      </div>
    );
  }

  // Main component render
  return (
    <div className="relative">
      {/* Stage Background and Particles */}
      <StageBackground />
      <DanceParticles />
      
      <div 
        className="fixed inset-0 flex flex-col bg-black text-white no-overscroll"
        style={{ 
          height: '100vh', 
          width: '100%', 
          overflow: 'hidden',
          filter: isBlurred ? 'blur(20px)' : 'none',
          transition: 'filter 0.3s ease'
        }}
      >
        {/* Combined Judge Station and Routine Header */}
        <div className="flex-shrink-0 px-4 pt-4 pb-1 relative z-20">
          <UnifiedStationHeader
            judgeProfile={judgeProfile || undefined}
            effectiveJudgeId={effectiveJudgeId}
            judgePosition={effectivePosition}
            connected={connected}
            socketStatus={socketStatus}
            competitionCode={competitionCode}
            isBlurred={isBlurred}
            onToggleBlur={() => toggleBlur()}
            isReconnecting={isReconnecting}
            reconnectAttempts={reconnectAttempts}
            currentRoutine={currentRoutine ? {
              ...currentRoutine,
              routineNumber: currentRoutine.number || currentRoutine.routineNumber || '',
              name: getRoutineName(currentRoutineData),
              performerName: getPerformerName(currentRoutineData),
              studioName: getStudioName(currentRoutineData),
              studioCode: getStudioCode(currentRoutineData),
              category: currentRoutine.category || currentRoutine['Category'] || '',
              division: currentRoutine.division || currentRoutine['Division'] || '',
              type: currentRoutine.type || currentRoutine['Type'] || '',
              ageGroup: currentRoutine.ageGroup || currentRoutine['Age Group'] || ''
            } : null}
            canNavigatePrevious={currentIndex > 0}
            canNavigateNext={currentIndex < routines.length - 1}
            onNavigatePrevious={handlePreviousRoutine}
            onNavigateNext={handleNextRoutine}
            timerProgress={timerProgress}
          />
        </div>

        {/* Main content area - flex-grow to fill available space */}
        <div className="flex-grow flex flex-col min-h-0 overflow-hidden relative z-20">
          {/* Scoring Rules Banner */}
          {scoringRules && (
            <div className="flex-shrink-0 mb-1 px-6">
              <ScoringRulesBanner scoringRules={scoringRules} />
            </div>
          )}


          {/* Main content grid - desktop layout - always use simple scoring layout */}
          <div className={`flex-grow min-h-0 grid grid-cols-3 ${layout.gap} overflow-hidden pb-6 pt-4`}>
            {registrationRulesLoading || loading ? (
              // Skeleton loading state while async data loads
              <motion.div
                key="skeleton"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="contents"
              >
                <SimpleScoringLayoutSkeleton 
                  layout={layout}
                  showStyleCard={true} // Show style card skeleton during loading since we don't know yet
                />
              </motion.div>
            ) : (
              // Actual SimpleScoringLayout once data is loaded
              <motion.div
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="contents"
              >
                <SimpleScoringLayout
                layout={layout}
                currentRoutine={currentRoutineData}
                currentIndex={currentIndex}
                routines={routines.map(convertRoutineToRoutineData).filter(Boolean) as RoutineData[]}
                scores={scores}
                comments={comments}
                isSubmitted={isSubmitted}
                lastUpdateTime={lastUpdateTime}
                judgeProfile={judgeProfile}
                effectiveJudgeId={effectiveJudgeId}
                styleScoreSheets={styleScoreSheets as StyleScoreSheet[]}
                styleCategories={styleCategories as StyleCategory[]}
                styleFeedback={currentRoutineFeedback}
                scoringMode={scoringMode}
                onScoreUpdate={handleScoreUpdate}
                onScoreSubmit={handleScoreSubmit}
                onRoutineSelect={handleRoutineSelect}
                onRoutinesUpload={handleRoutinesUpload}
                onStyleFeedbackUpdate={handleStyleFeedbackUpdate}
                isRoutineLocked={isRoutineLocked}
              />
              </motion.div>
            )}
          </div>
        </div>

        <RecordingIndicator />
      </div>

      {/* Custom Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
        onSave={saveAndContinue}
        title="Unsaved Changes"
        message="You have unsaved scores that will be lost if you continue. Would you like to save them first?"
        showSaveOption={true}
      />

      {/* Privacy lock screen overlay */}
      {isBlurred && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div 
            className="text-center p-8 rounded-2xl bg-black/80 backdrop-blur-xl border border-white/20"
          >
            <button
              onClick={() => toggleBlur()}
              className="cursor-pointer"
              aria-label="Click to unlock screen"
            >
              <div>
                <Lock size={64} className="mx-auto mb-4 text-pink-400 hover:text-pink-300 transition-colors" />
              </div>
            </button>
            <h2 className="text-2xl font-bold text-white mb-2">Screen Locked</h2>
            <p className="text-white/70 mb-4">Click the lock icon above or press Ctrl+L to unlock</p>
            <div className="text-xs text-white/50">
              Privacy mode activated
            </div>
          </div>
        </div>
      )}

      {/* Toast notification for alerts */}
      {toast && (
        <div className={`fixed bottom-4 right-4 max-w-sm p-3 rounded-lg shadow-lg text-sm z-50 ${
          toast.type === 'success' 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/70 dark:text-green-300'
            : toast.type === 'warning'
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/70 dark:text-yellow-300'
              : toast.type === 'info'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/70 dark:text-blue-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/70 dark:text-red-300'
        }`}>
          <div className="flex items-start">
            {toast.type === 'success' 
              ? <CheckCircle size={16} className="mr-2 flex-shrink-0" />
              : <AlertCircle size={16} className="mr-2 flex-shrink-0" />
            }
            <div>
              <p className="text-sm">{toast.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JudgeStationPage;
